/**
 * Maps numeric codes to their corresponding string keys
 */
export enum SEARCH_METER_CODE {
  NOT_FOUND = 404,
  OFFLINE = 406,
  ALREADY_COMMISSIONED = 409,
  READY_TO_GO = 200,
  FORBIDDEN = 403,
  NOT_COMMISSIONED = 423,
  COMMISSIONING = 205,
  NEED_POWER_CYCLE = 206,
}

export const SEARCH_METER_INTERVAL_MS = 30 * 1000; // 30 s
export const SEARCH_METER_TIMEOUT_MS = 6 * 60 * 1000; // 6 min

export const SOFTWARE_UPDATE_INTERVAL_MS = 10 * 1000; // 10 s
export const SOFTWARE_UPDATE_TIMEOUT_MS = 35 * 60 * 1000; // 35 min

export const UPLOAD_CONFIG_INTERVAL_MS = 10 * 1000; // 10 s
export const UPLOAD_CONFIG_TIMEOUT_MS = 10 * 60 * 1000; // 10 min
export const COMMISSION_LOG_INTERVAL_MS = 10 * 1000; // 10 s
export const COMMISSION_LOG_TIMEOUT_MS = 10 * 60 * 1000; // 10 min
export const COMMISSION_RESULT_LOG_INTERVAL_MS = 10 * 1000; // 10 s
export const COMMISSION_RESULT_LOG_TIMEOUT_MS = 10 * 60 * 1000; // 10 min
/** Re-issue `commission-get-results` if the results poll stays pending (lost command recovery). */
export const COMMISSION_RESULT_COMMAND_RESEND_INTERVAL_MS = COMMISSION_RESULT_LOG_INTERVAL_MS * 2;

/**
 * Connection type values must be identical to the value in src/modules/biz/commission/dto/CommissionDto.ts in main-server
 */
export enum CONNECTION_TYPE {
  ONE_TRIPLE = '1x3',
  THREE_SINGLE = '3x1',
  NO_CONNECTION = 'n/a',
  NO_SELECTED = '',
}

export const TERMINAL_NAME_NO_CONNECTION = 'No Connection';
export const TERMINAL_NO_CONNECTION_DISPLAY_TEXT = 'No live connection detected';
export const TERMINAL_CONNECTED_DISPLAY_TEXT = 'Live connection detected';

export const GROUP_INDEXES = [0, 1, 2, 3, 4] as const;
export const PHASE_INDEXES = [0, 1, 2] as const;
export enum PHASE {
  L1 = 'L1',
  L2 = 'L2',
  L3 = 'L3',
}

export enum COMMISSION_CONFIG_STATUS {
  SUCCESS = 'pass',
  PENDING = 'pending',
  FAILED = 'fail',
}

export const COMMISSION_CONFIG_STATUS_TEXT = {
  [COMMISSION_CONFIG_STATUS.SUCCESS]: 'SolShare configuration updated successfully.',
  [COMMISSION_CONFIG_STATUS.PENDING]: 'Updating SolShare configuration...',
  [COMMISSION_CONFIG_STATUS.FAILED]: 'Failed to update SolShare configuration.',
};

export enum COMMISSION_STATUS {
  READY = 'ready',
  RUNNING = 'running',
  COMPLETE = 'complete',
  FAILED = 'fail',
}

export const COMMISSION_STATUS_TEXT = {
  [COMMISSION_STATUS.READY]: 'Ready to start testing.',
  [COMMISSION_STATUS.RUNNING]: 'Commissioning SolShare in progress.',
  [COMMISSION_STATUS.COMPLETE]: 'Commissioning test completed successfully.',
  [COMMISSION_STATUS.FAILED]: 'Failed to commission SolShare.',
};

export enum COMMISSION_RESULT_STATUS {
  SUCCESS = 'complete',
  PENDING = 'running',
  FAILED = 'fail',
}

export const COMMISSION_RESULT_STATUS_TEXT = {
  [COMMISSION_RESULT_STATUS.SUCCESS]: 'Commissioning SolShare completed successfully.',
  [COMMISSION_RESULT_STATUS.PENDING]: 'Getting commission results...',
  [COMMISSION_RESULT_STATUS.FAILED]: 'Failed to commission SolShare.',
};

export enum COMMISSION_COMPLETE_STATUS {
  SUCCESS = 'success',
  PENDING = 'pending',
  FAILED = 'fail',
}

export enum COMMISSION_RESULT_VERDICT {
  PASS = 'pass',
  FAIL = 'fail',
}

export enum COMMISSION_RESULT_FAIL_REASON {
  inverter_off_timeout = 'inverter_off_timeout',
  grid_voltage_invalid = 'grid_voltage_invalid',
  safe_state_timeout = 'safe_state_timeout',
  measurement_timeout = 'measurement_timeout',
  generic_timeout = 'generic_timeout',
  grid_unstable_start = 'grid_unstable_start',
  solar_unexpected_start = 'solar_unexpected_start',
  solar_unexpected_end = 'solar_unexpected_end',
  grid_unstable_end = 'grid_unstable_end',
  grid_unstable_during = 'grid_unstable_during',
  solar_observed_bad = 'solar_observed_bad',
  ct_swapped = 'ct_swapped',
  ct_reversed = 'ct_reversed',
  current_diff_unexpected = 'current_diff_unexpected',
  ct_missing = 'ct_missing',
}

export const TERMINALS_LIST = [
  'L1-1',
  'L1-2',
  'L1-3',
  'L1-4',
  'L1-5',
  'L2-1',
  'L2-2',
  'L2-3',
  'L2-4',
  'L2-5',
  'L3-1',
  'L3-2',
  'L3-3',
  'L3-4',
  'L3-5',
] as const;

/** Connection state when a terminal has been decommissioned. */
export const CONNECTION_STATE_DECOMMISSIONED = 'DECOMMISSIONED';

/** 
 * MQTT command for POST /v3/meters/:serial/command. 
 * Commented out commands are not currently used in SolCentre.
 */
export const MQTT_COMMAND = {
  // PING: 'ping', 
  // PING_FORWARDER: 'ping-forwarder', 
  ACTOR_PRESENT: 'actor-present',
  COMMISSION_ENTER: 'commission-enter',
  COMMISSION_START: 'commission-start',
  COMMISSION_GET_RESULTS: 'commission-get-results',
  COMMISSION_COMPLETE: 'commission-complete',
  COMMISSION_DECOMMISSION: 'commission-decommission',
  UPDATE_CONFIG: 'update-config',
  // WEBTERM: 'webterm',
} as const;

/** Poll interval / timeout for recommission prepare waits. */
export const RECOMMISSION_PREPARE_POLL_INTERVAL_MS = 5 * 1000;
export const RECOMMISSION_PREPARE_POLL_TIMEOUT_MS = 10 * 60 * 1000;

export type RecommissionPrepareStepId =
  | 'actorPresent'
  | 'commissionEnter'
  | 'waitPreCommissioned'
  | 'commissionDecommission'
  | 'waitDecommissioned';

/**
 * Editable prepare-step copy, delays, and progress budgets.
 * Change `message` / `delayMs` / `progress` / `pollStep` here without touching orchestration logic.
 *
 * `progress` — percentage points added when the step succeeds (poll steps: max budget).
 * `pollStep` — optional; each poll attempt adds this many points, clamped to `progress`.
 */
export type RecommissionPrepareStepConfig = {
  id: RecommissionPrepareStepId;
  message: string;
  delayMs: number;
  progress: number;
  pollStep?: number;
};

export function getRecommissionPrepareStepIndex(id: RecommissionPrepareStepId): number {
  return RECOMMISSION_PREPARE_STEPS.findIndex((entry) => entry.id === id);
}

/** Index of the next step to run after `lastCompletedStepId`, or 0 when starting fresh. */
export function getNextRecommissionPrepareStepIndex(
  lastCompletedStepId: RecommissionPrepareStepId | null,
): number {
  if (lastCompletedStepId === null) {
    return 0;
  }

  const completedIndex = getRecommissionPrepareStepIndex(lastCompletedStepId);
  if (completedIndex === -1) {
    return 0;
  }

  return completedIndex + 1;
}

export const RECOMMISSION_PREPARE_STEPS: readonly RecommissionPrepareStepConfig[] = [
  {
    id: 'actorPresent',
    message: 'Connecting to your SolShare...',
    delayMs: 1000,
    progress: 10,
  },
  {
    id: 'commissionEnter',
    message: 'Preparing the SolShare for setup...',
    delayMs: 1000,
    progress: 10,
  },
  {
    id: 'waitPreCommissioned',
    message: 'Waiting for the device to be ready...',
    delayMs: 1000,
    progress: 20,
    pollStep: 5,
  },
  {
    id: 'commissionDecommission',
    message: 'Removing the previous configuration...',
    delayMs: 1000,
    progress: 30,
  },
  {
    id: 'waitDecommissioned',
    message: 'Completing the setup...',
    delayMs: 1000,
    progress: 30,
    pollStep: 10,
  },
] as const;

/**
 * --------------------------------------------------------------------------------------------
 * EVERYTHING below is copy pasted from old com app src/utils/constants.js
 * Bring it above the line when referencing in code, so we know at the end what is not used.
 * --------------------------------------------------------------------------------------------
 */

/**
 * Used to get user region from IP address
 * this is for display different contact details in different region
 */
export const REGION_CODE = {
  Oceania: 'Oceania',
  America: 'Americas',
  Europe: 'Europe',
};

export const PHONE_NUMBERS = {
  Oceania: '+61 (0) 3 7038 0686',
  America: '+1 (213) 347-4293',
  Europe: '+44 20 8156 2818',
};

export const INVERTERS = {
  Fronius: 'Fronius',
  SolarEdge: 'SolarEdge',
  SMA: 'SMA',
  Other: 'Other',
};

export const UNEQUAL_ALLOCATION_TYPE = {
  PERCENT: '%',
  KW: 'kW',
  FT: 'ft',
};

export const CT_RATING = {
  A200: 200,
  A120: 120,
  A75: 75,
};

export { SOLSHARE_VERSION, GENERATION } from '@/shared/constants/solshare';

export const COMMISSION_LOG_ERROR_CODE = {
  UNKNOWN: 'unknown',
  SWAPPED: 'swapped',
  NO_GRID: 'nogrid',
  REVERSED: 'reverse',
  NO_SOLAR: 'nosolar',
  CONFIG_FAIL: 'configfail',
  INVERTER_TRIP: 'trip',
  WRONG_CT: 'wrongct',
  NO_VOLTAGE: 'novoltage',
  INVERTER_NOT_OUTPUTTING: 'nooutput',
  PHASE_SWAPPED: 'phaseswap',
  TIMEOUT: 'timeout',
};

export const COMMISSION_STAGE = {
  FIRST: 'config',
  SECOND: 'commission',
};

export const WIFI_STATUS_INTERVAL_MS = 30 * 1000; // 30s
export const UPLOAD_CONNECTION_DEBOUNCE_MS = 5000; // 5s
export const COMMISSIONING_PROCESS_TIMEOUT = 20 * 60 * 1000; // 20 mins

export const USER_ROLE = {
  COMMISSIONER: 'Commissioner',
  PROVIDER: 'Provider',
};

export const FAULT_TYPE = {
  OFFLINE: 'Offline',
  NO_SOLAR: 'No Solar Output',
  BATTERY_TRIP: 'Battery Trip',
  RELAY_SHUT: 'Relay Shut',
  INVERTER_TRIP: 'Inverter Trip',
  FAILED_CT: 'Failed CT',
};

export const DISPLAY_FAULT = [FAULT_TYPE.OFFLINE, FAULT_TYPE.NO_SOLAR];

export const FAULT_DESCRIPTION = {
  [FAULT_TYPE.OFFLINE]: {
    name: 'No network access',
    desc: 'Please check the Wi-Fi network the SolShare is connected to is powered and functioning.',
  },
  [FAULT_TYPE.NO_SOLAR]: {
    name: 'No solar output',
    desc: 'No solar has been generated for at least 24 hours, please check your inverter status portal to see if there are any faults in the system. If you need any support please give us a call.',
  },
};
